import { useState } from 'react';
import {
  Alert,
  Paper,
  Stack,
  Tabs,
  Tab,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Button,
  FormControlLabel,
  Checkbox,
  Box,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import dayjs from 'dayjs';
import { useNavigate, useParams } from 'react-router-dom';
import PageLayout from '../common/components/PageLayout';
import SettingsMenu from './components/SettingsMenu';
import useSettingsStyles from './common/useSettingsStyles';
import { useEffectAsync } from '../reactHelper';
import fetchOrThrow from '../common/util/fetchOrThrow';
import { MAINTENANCE_ENDPOINTS, MAINTENANCE_ROUTES } from './maintenanceConstants';

const MaintenanceRecordPage = () => {
  const { classes } = useSettingsStyles();
  const navigate = useNavigate();
  const { id } = useParams(); // "new" ou um id numérico

  const [devices, setDevices] = useState([]);

  const [item, setItem] = useState({
    deviceId: null,
    deviceName: '',
    service: '',
    category: '',
    status: 'Concluido',
    date: dayjs().format('YYYY-MM-DD'),
    odometer: '',
    nextDate: '',
    nextOdometer: '',
    totalCost: '',
    workshop: '',
    internalResponsible: '',
    notes: '',
  });

  const [createReminder, setCreateReminder] = useState(false);
  const [reminderType, setReminderType] = useState('totalDistance'); // 'totalDistance' | 'hours'
  const [activeTab, setActiveTab] = useState('identification');

  const isEdit = !!id && id !== 'new';

  // Carrega devices do Traccar
  useEffectAsync(async () => {
    const response = await fetchOrThrow(MAINTENANCE_ENDPOINTS.devices);
    const data = await response.json();
    setDevices(data);
  }, []);

  // Helper p/ pegar tanto camelCase quanto snake_case
  const getField = (obj, camel, snake) =>
    obj[camel] !== undefined && obj[camel] !== null
      ? obj[camel]
      : obj[snake];

  // Se for edição, carrega o registro do serviço Node
  useEffectAsync(async () => {
    if (!isEdit) {
      return;
    }

    const response = await fetchOrThrow(`${MAINTENANCE_ENDPOINTS.maintenanceHistory}/${id}`);
    const data = await response.json();

    const rawDate = getField(data, 'date', 'event_date');
    const rawNextDate = getField(data, 'nextDate', 'next_date');

    setItem({
      deviceId: getField(data, 'deviceId', 'device_id') ?? null,
      deviceName: getField(data, 'deviceName', 'device_name') ?? '',
      service: data.service || '',
      category: data.category || '',
      status: data.status || 'Concluido',
      date: rawDate ? dayjs(rawDate).format('YYYY-MM-DD') : '',
      odometer: getField(data, 'odometer', 'odometer') ?? '',
      nextDate: rawNextDate ? dayjs(rawNextDate).format('YYYY-MM-DD') : '',
      nextOdometer: getField(data, 'nextOdometer', 'next_odometer') ?? '',
      totalCost: getField(data, 'totalCost', 'total_cost') ?? '',
      workshop: getField(data, 'workshop', 'workshop') ?? '',
      internalResponsible: getField(data, 'internalResponsible', 'internal_responsible') ?? '',
      notes: data.notes ?? '',
    });
  }, [isEdit, id]);

  const handleSave = async () => {
    const method = isEdit ? 'PUT' : 'POST';
    const url = isEdit
      ? `/maintenance-api/maintenance-records/${id}`
      : MAINTENANCE_ENDPOINTS.maintenanceHistory;

    const payload = {
      ...item,
      date: item.date || null,
      nextDate: item.nextDate || null,
    };

    // 1) Salva o histórico na nossa API Node
    const historyResponse = await fetchOrThrow(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    let savedRecordId = id;
    if (!isEdit) {
      const saved = await historyResponse.json();
      savedRecordId = saved.id;
    }

    // 2) Se marcado, cria um lembrete de manutenção no Traccar
    if (createReminder && item.deviceId) {
      const hasNextKm = item.nextOdometer !== '' && item.nextOdometer !== null;
      const hasCurrentKm = item.odometer !== '' && item.odometer !== null;

      // Só faz sentido criar lembrete se tivermos pelo menos odômetro e próximo odômetro
      if (hasNextKm && hasCurrentKm) {
        const currentVal = Number(item.odometer) || 0;
        const nextVal = Number(item.nextOdometer) || 0;

        let startRaw = 0;
        let periodRaw = 0;

        if (reminderType === 'totalDistance') {
          // km -> metros
          const currentMeters = currentVal * 1000;
          const nextMeters = nextVal * 1000;
          startRaw = currentMeters;
          periodRaw = nextMeters > currentMeters ? (nextMeters - currentMeters) : 0;
        } else if (reminderType === 'hours') {
          // horas em unidades de horas
          startRaw = currentVal;
          periodRaw = nextVal > currentVal ? (nextVal - currentVal) : 0;
        }

        const nameParts = [];
        if (item.service) nameParts.push(item.service);
        if (item.deviceName) nameParts.push(item.deviceName);
        const name = nameParts.join(' - ') || 'Manutenção';

        const attributes = {
          origem: 'maintenance-history',
        };
        if (savedRecordId) {
          attributes.historyRecordId = Number(savedRecordId);
        }

        // 2.1 Criar maintenance no Traccar
        const maintenanceResp = await fetchOrThrow(MAINTENANCE_ENDPOINTS.traccarMaintenance, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name,
            type: reminderType, // 'totalDistance' ou 'hours'
            start: startRaw,
            period: periodRaw,
            attributes,
          }),
        });

        const maintenance = await maintenanceResp.json();

        // 2.2 Vincular maintenance ao device
        await fetchOrThrow(MAINTENANCE_ENDPOINTS.permissions, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            deviceId: item.deviceId,
            maintenanceId: maintenance.id,
          }),
        });
      }
    }

    navigate(MAINTENANCE_ROUTES.historyList);
  };

  const handleCancel = () => {
    navigate(MAINTENANCE_ROUTES.historyList);
  };

  const handleDelete = async () => {
    if (!isEdit) return;

    const confirmed = window.confirm('Tem certeza que deseja excluir esta manutenção?');
    if (!confirmed) return;

    await fetchOrThrow(`/maintenance-api/maintenance-records/${id}`, {
      method: 'DELETE',
    });

    navigate(MAINTENANCE_ROUTES.historyList);
  };

  const selectedDevice =
    devices.find((d) => String(d.id) === String(item.deviceId)) || null;

  return (
    <PageLayout
      menu={<SettingsMenu />}
      breadcrumbs={['settingsTitle', 'sharedMaintenance']}
    >
      <Paper elevation={0} sx={{ border: 1, borderColor: 'divider', overflow: 'hidden' }}>
        <Box sx={{ px: 3, py: 2.5, bgcolor: 'background.default' }}>
          <Typography variant="h6">
            {isEdit ? 'Editar ordem de serviço' : 'Nova ordem de serviço'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Dados organizados por etapa para facilitar o preenchimento e reduzir erros.
          </Typography>
        </Box>

        <Tabs
          value={activeTab}
          onChange={(event, value) => setActiveTab(value)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ px: 2, borderTop: 1, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab icon={<DirectionsCarIcon />} iconPosition="start" label="Identificação" value="identification" />
          <Tab icon={<EventRepeatIcon />} iconPosition="start" label="Agenda e medição" value="schedule" />
          <Tab icon={<ReceiptLongIcon />} iconPosition="start" label="Custos e execução" value="execution" />
          <Tab icon={<NotificationsActiveIcon />} iconPosition="start" label="Lembrete" value="reminder" />
        </Tabs>

        <Box className={classes.details} sx={{ p: 3 }}>
          {activeTab === 'identification' && (
            <Stack spacing={2.5}>
              <Box>
                <Typography variant="subtitle1">Quem será atendido?</Typography>
                <Typography variant="body2" color="text.secondary">
                  Selecione o veículo e classifique a ordem de serviço.
                </Typography>
              </Box>

              <Autocomplete
                options={devices}
                getOptionLabel={(option) => option?.name || ''}
                value={selectedDevice}
                isOptionEqualToValue={(option, value) =>
                  !!value && String(option.id) === String(value.id)
                }
                onChange={(e, value) =>
                  setItem((prev) => ({
                    ...prev,
                    deviceId: value ? value.id : null,
                    deviceName: value ? value.name : '',
                  }))
                }
                renderInput={(params) => (
                  <TextField {...params} label="Veículo" />
                )}
              />

              <TextField
                label="Serviço"
                value={item.service}
                onChange={(e) => setItem({ ...item, service: e.target.value })}
              />

              <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
                <FormControl>
                  <InputLabel>Categoria</InputLabel>
                  <Select
                    label="Categoria"
                    value={item.category}
                    onChange={(e) => setItem({ ...item, category: e.target.value })}
                  >
                    <MenuItem value="Preventiva">Preventiva</MenuItem>
                    <MenuItem value="Corretiva">Corretiva</MenuItem>
                    <MenuItem value="Legal">Legal</MenuItem>
                  </Select>
                </FormControl>

                <FormControl>
                  <InputLabel>Status</InputLabel>
                  <Select
                    label="Status"
                    value={item.status}
                    onChange={(e) => setItem({ ...item, status: e.target.value })}
                  >
                    <MenuItem value="Planejado">Planejado</MenuItem>
                    <MenuItem value="Concluido">Concluído</MenuItem>
                    <MenuItem value="Cancelado">Cancelado</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Stack>
          )}

          {activeTab === 'schedule' && (
            <Stack spacing={2.5}>
              <Box>
                <Typography variant="subtitle1">Quando aconteceu e quando retorna?</Typography>
                <Typography variant="body2" color="text.secondary">
                  Registre data, leitura atual e próxima previsão por data, km ou horas.
                </Typography>
              </Box>

              <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
                <TextField
                  label="Data do Serviço"
                  type="date"
                  value={item.date || ''}
                  onChange={(e) => setItem({ ...item, date: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />

                <TextField
                  label="Km / Horas do Serviço"
                  type="number"
                  value={item.odometer || ''}
                  onChange={(e) => setItem({ ...item, odometer: e.target.value })}
                />

                <TextField
                  label="Próxima Manutenção (Data)"
                  type="date"
                  value={item.nextDate || ''}
                  onChange={(e) => setItem({ ...item, nextDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />

                <TextField
                  label="Próxima Manutenção (Km / Horas)"
                  type="number"
                  value={item.nextOdometer || ''}
                  onChange={(e) => setItem({ ...item, nextOdometer: e.target.value })}
                />
              </Box>
            </Stack>
          )}

          {activeTab === 'execution' && (
            <Stack spacing={2.5}>
              <Box>
                <Typography variant="subtitle1">Execução e financeiro</Typography>
                <Typography variant="body2" color="text.secondary">
                  Centralize custos, oficina, responsável e observações da ordem.
                </Typography>
              </Box>

              <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
                <TextField
                  label="Valor Total"
                  type="number"
                  value={item.totalCost || ''}
                  onChange={(e) => setItem({ ...item, totalCost: e.target.value })}
                />

                <TextField
                  label="Oficina"
                  value={item.workshop || ''}
                  onChange={(e) => setItem({ ...item, workshop: e.target.value })}
                />

                <TextField
                  label="Responsável Interno"
                  value={item.internalResponsible || ''}
                  onChange={(e) => setItem({ ...item, internalResponsible: e.target.value })}
                  sx={{ gridColumn: { sm: '1 / -1' } }}
                />
              </Box>

              <TextField
                label="Observações"
                value={item.notes || ''}
                onChange={(e) => setItem({ ...item, notes: e.target.value })}
                multiline
                minRows={4}
              />
            </Stack>
          )}

          {activeTab === 'reminder' && (
            <Stack spacing={2.5}>
              <Box>
                <Typography variant="subtitle1">Automação do próximo lembrete</Typography>
                <Typography variant="body2" color="text.secondary">
                  Crie automaticamente um lembrete no Traccar ao salvar esta ordem de serviço.
                </Typography>
              </Box>

              <Alert severity="info">
                O lembrete só será criado quando houver veículo, medição atual e próxima medição preenchidos.
              </Alert>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={createReminder}
                    onChange={(e) => setCreateReminder(e.target.checked)}
                  />
                }
                label="Criar novo lembrete de manutenção ao salvar"
              />

              {createReminder && (
                <FormControl>
                  <InputLabel>Tipo do lembrete</InputLabel>
                  <Select
                    label="Tipo do lembrete"
                    value={reminderType}
                    onChange={(e) => setReminderType(e.target.value)}
                  >
                    <MenuItem value="totalDistance">Distância total (km)</MenuItem>
                    <MenuItem value="hours">Horas de motor</MenuItem>
                  </Select>
                </FormControl>
              )}
            </Stack>
          )}
        </Box>
      </Paper>

      <Box
        sx={{
          display: 'flex',
          gap: 2,
          mt: 2,
          justifyContent: 'flex-end',
        }}
      >
        {isEdit && (
          <Button color="error" onClick={handleDelete}>
            Excluir
          </Button>
        )}
        <Button onClick={handleCancel}>
          Cancelar
        </Button>
        <Button variant="contained" color="primary" onClick={handleSave}>
          Salvar
        </Button>
      </Box>
    </PageLayout>
  );
};

export default MaintenanceRecordPage;
